import { useEffect, useState } from 'react';
import { Flame, Target, Check, Shield, X } from 'lucide-react';

const ICONS = { flame: Flame, target: Target, check: Check, shield: Shield };

/**
 * MilestoneCelebration - full-screen overlay shown when a scan triggers a milestone.
 *
 * Props:
 *   milestone: { kind, value, title, subtitle, icon }
 *   onDismiss: () => void
 *   delay: number  (ms before showing — lets the verdict flash play first)
 */
export default function MilestoneCelebration({ milestone, onDismiss, delay = 1000 }) {
  const [visible, setVisible] = useState(false);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!milestone) return;
    const t = setTimeout(() => {
      setVisible(true);
      // Haptic celebration pattern
      if (navigator.vibrate) navigator.vibrate([60, 50, 60, 50, 120]);
    }, delay);
    return () => clearTimeout(t);
  }, [milestone, delay]);

  // Leaderboard-style number roll
  useEffect(() => {
    if (!visible || !milestone) return;
    const target = milestone.value;
    const duration = 900;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, milestone]);

  if (!milestone || !visible) return null;
  const Icon = ICONS[milestone.icon] || Target;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-6"
      data-testid="milestone-overlay"
      style={{
        background: 'radial-gradient(circle at center, rgba(0,230,118,0.18) 0%, rgba(10,10,10,0.92) 55%, rgba(10,10,10,0.98) 100%)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'fade-up 320ms cubic-bezier(0.4,0,0.2,1) forwards'
      }}
    >
      {/* Confetti burst */}
      <ConfettiBurst />

      <button
        onClick={onDismiss}
        data-testid="milestone-dismiss"
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-qc-surface border border-qc-border text-qc-text-dim flex items-center justify-center"
        style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
        aria-label="Dismiss"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="relative z-10 flex flex-col items-center text-center max-w-sm">
        <p className="text-xs font-black uppercase tracking-[0.3em] text-qc-green mb-4 animate-fade-up">
          Milestone unlocked
        </p>

        {/* Icon halo */}
        <div className="relative mb-6 animate-verdict-pop">
          <div className="absolute inset-0 rounded-full bg-qc-green/30 blur-3xl scale-150" />
          <div className="absolute inset-0 rounded-full border-2 border-qc-green/40 animate-green-pulse" />
          <div className="relative w-28 h-28 rounded-full bg-qc-green flex items-center justify-center shadow-[0_0_60px_rgba(0,230,118,0.7)]">
            <Icon className="w-14 h-14 text-qc-bg" strokeWidth={3} />
          </div>
        </div>

        {/* Big number — leaderboard roll */}
        <div
          className="text-[7rem] sm:text-[8rem] font-black leading-none tracking-tighter text-qc-text tabular-nums"
          data-testid="milestone-value"
          style={{ textShadow: '0 4px 32px rgba(0,230,118,0.4)' }}
        >
          {displayValue}
        </div>

        <h2
          className="text-2xl sm:text-3xl font-black tracking-tight text-qc-green mt-2 mb-2"
          data-testid="milestone-title"
        >
          {milestone.title}
        </h2>
        <p className="text-base text-qc-text-dim leading-snug" data-testid="milestone-subtitle">
          {milestone.subtitle}
        </p>

        <button
          onClick={onDismiss}
          data-testid="milestone-continue"
          className="mt-8 capture-btn h-12 px-8 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-full font-extrabold text-base shadow-[0_8px_24px_rgba(0,230,118,0.4)]"
        >
          Keep going
        </button>
      </div>
    </div>
  );
}

// Decorative confetti pieces — pure CSS, no library
function ConfettiBurst() {
  const pieces = Array.from({ length: 24 });
  const colors = ['#00E676', '#FFC107', '#FFFFFF', '#00E676', '#FFC107'];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {pieces.map((_, i) => {
        const angle = (i / pieces.length) * Math.PI * 2;
        const dist = 180 + Math.random() * 160;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const color = colors[i % colors.length];
        const delay = (i % 6) * 20;
        const size = 6 + Math.random() * 6;
        // Confetti pieces are decorative + static — no reorder, so stable prefix + i is fine.
        return (
          <span
            key={`confetti-${i}`}
            className="absolute top-1/2 left-1/2 rounded-sm"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              backgroundColor: color,
              transform: 'translate(-50%, -50%)',
              animation: `confetti-fly 900ms ${delay}ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards`,
              ['--dx']: `${dx}px`,
              ['--dy']: `${dy}px`,
              opacity: 0
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fly {
          0% { transform: translate(-50%, -50%) scale(0.4) rotate(0deg); opacity: 0; }
          15% { opacity: 1; }
          100% {
            transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1) rotate(540deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
